import { AuthService } from '@account-users';
import { UserRoles } from '@account-users/account/models';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';

import {
  CreateCommandParams,
  CreateCommandResponse,
  CreateCommandService,
} from '../../services/create-command';
import { LogCommand, LogCommandService } from '../../services/log-command';
import {
  InviteCommandResponse,
  InviteCommandService,
} from '../../services/invite-command';
import {
  StatusCommandResponse,
  StatusCommandService,
} from '../../services/status-command';

import { CreateWizardCommand } from '../../services/create-wizard-command/create-wizard-command';

import {
  BootstrapError,
  CreateErrorBase,
  CreateRenewTokenError,
  LogError,
  InviteAdminError,
} from '../../models';

enum CommandState {
  Success = 'Success',
  Pending = 'Pending',
  Progress = 'Progress',
  Failed = 'Failed',
}

@Component({
  selector: 'app-account-create-wizard',
  templateUrl: './account-create-wizard.component.html',
  styleUrls: ['./account-create-wizard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountCreateWizardComponent implements OnInit {
  @Input() params: CreateWizardCommand;
  @Output() accountCreateConfirmation = new EventEmitter<{
    accountId: string;
  }>();

  public readonly CommandState = CommandState;
  public createCommandState = CommandState.Pending;
  public bootstrapCommandState = CommandState.Pending;
  public inviteCommandState = CommandState.Pending;

  private _logCommand = this.injector.get(LogCommandService).build({});

  get isCreateInProgress() {
    return this.createCommandState === CommandState.Progress;
  }

  get isBootstrapInProgress() {
    return this.bootstrapCommandState === CommandState.Progress;
  }

  get isInviteInProgress() {
    return this.inviteCommandState === CommandState.Progress;
  }

  get isAllStepsSuccess() {
    return (
      this.createCommandState === CommandState.Success &&
      this.bootstrapCommandState === CommandState.Success &&
      this.inviteCommandState === CommandState.Success
    );
  }

  constructor(
    public readonly activeModal: NgbActiveModal,
    private readonly toastrService: ToastrService,
    private readonly authService: AuthService,
    private readonly injector: Injector,
    private readonly cd: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    const { createCommand } = this.params;
    this.createAccountCommandRunner(createCommand).run();
  }

  // Invite admin user to account
  private inviteCommandRunner(accountId: string, adminEmail: string) {
    const onStart = () => (this.inviteCommandState = CommandState.Progress);
    const onEnd = () => {
      this.inviteCommandState = CommandState.Success;
      this.cd.markForCheck();
    };
    const onFailed = (err: InviteAdminError) => {
      this.inviteCommandState = CommandState.Failed;
      this.logError({
        accountId,
        id: this.params?.requestId,
        userId: this.authService.profile.userId,
        message: err?.message,
      });
      this.toastrService.error(err?.message, err?.messageHeader, {
        disableTimeOut: true,
        closeButton: true,
      });
      this.cd.markForCheck();
    };
    const onSuccess = (response: InviteCommandResponse) => {
      const { entryParams } = response;
      this.activeModal.close();
      this.accountCreateConfirmation.emit({ accountId: entryParams.accountId });
    };

    const inviteCommand = this.injector.get(InviteCommandService).build({
      onStart: () => onStart(),
      onEnd: () => onEnd(),
      onFailed: (err: InviteAdminError) => onFailed(err),
      onSuccess: (response: InviteCommandResponse) => onSuccess(response),
    });

    return {
      run: () =>
        inviteCommand.execute({
          accountId,
          email: adminEmail,
          toolsPermissions: [],
          roles: [UserRoles.AccountOwner, UserRoles.User],
        }),
    };
  }

  // Bootstrap Account Check Status
  private boostrapStatusCommandRunner(accountId: string, adminEmail: string) {
    const onStart = () => (this.bootstrapCommandState = CommandState.Progress);
    const onEnd = () => {
      this.bootstrapCommandState = CommandState.Success;
      this.cd.markForCheck();
    };
    const onSuccess = (response: StatusCommandResponse) => {
      const { entryParams, id } = response;
      this.inviteCommandRunner(id, entryParams.adminEmail).run();
    };
    const onFailed = (err: BootstrapError) => {
      this.bootstrapCommandState = CommandState.Failed;
      this.logError({
        accountId,
        id: this.params?.requestId,
        userId: this.authService.profile.userId,
        message: err?.message,
      });
      this.toastrService.error(err?.message, err?.messageHeader, {
        disableTimeOut: true,
        closeButton: true,
      });
      this.cd.markForCheck();
    };

    const statusCommand = this.injector.get(StatusCommandService).build({
      onStart: () => onStart(),
      onEnd: () => onEnd(),
      onSuccess: (response: StatusCommandResponse) => onSuccess(response),
      onFailed: (err: BootstrapError) => onFailed(err),
    });
    return {
      run: () => statusCommand.execute({ accountId, adminEmail }),
    };
  }

  // Create Account Command
  private createAccountCommandRunner(createRequest: CreateCommandParams) {
    const onFailed = (err: CreateErrorBase) => {
      this.createCommandState = CommandState.Failed;
      let message: string;
      if (err instanceof CreateRenewTokenError) {
        message = err?.message;
      } else {
        message =
          err?.error?.status === 409
            ? (err?.config?.message as string)
            : err?.message;
      }
      this.logError({
        id: this.params?.requestId,
        userId: this.authService.profile.userId,
        message,
      });
      this.toastrService.error(message, err?.messageHeader, {
        disableTimeOut: true,
        closeButton: true,
      });
      this.cd.markForCheck();
    };
    const onStart = () => (this.createCommandState = CommandState.Progress);
    const onEnd = () => {
      this.createCommandState = CommandState.Success;
      this.cd.markForCheck();
    };
    const onSuccess = (response: CreateCommandResponse) => {
      const {
        entryParams: { adminEmail },
        id,
      } = response;

      this.boostrapStatusCommandRunner(id, adminEmail).run();
    };

    const createCommand = this.injector.get(CreateCommandService).build({
      onStart: () => onStart(),
      onEnd: () => onEnd(),
      onSuccess: (response: CreateCommandResponse) => onSuccess(response),
      onFailed: (err: CreateErrorBase) => onFailed(err),
    });
    return {
      run: () => createCommand.execute(createRequest),
    };
  }

  private logError(payload: LogCommand) {
    this._logCommand.execute(payload);
  }
}
